import React, { useState, useRef, useEffect } from "react"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import Input from "aldoo-ra/Input"
import Spinner from "aldoo-ra/Spinner"
import {
  PaperAirplaneIcon,
  PaperClipIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline"
import ChatResponsePresets from "./ChatResponsePresets"

const ChatArea = ({ onSendMessage, onSendAndResolve, sending = false }) => {
  const [messageText, setMessageText] = useState("")
  const [showPresets, setShowPresets] = useState(false)
  const [attachedFiles, setAttachedFiles] = useState([])
  const [showLeftGradient, setShowLeftGradient] = useState(false)
  const [showRightGradient, setShowRightGradient] = useState(false)
  const fileInputRef = useRef()
  const scrollContainerRef = useRef()

  // Detect if the user is on Mac or not
  const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0

  const handleSend = () => {
    if (!messageText.trim() && attachedFiles.length === 0) return

    onSendMessage({
      body: messageText,
      files: attachedFiles,
    })

    // Clear inputs after sending
    setMessageText("")
    setAttachedFiles([])
  }

  const handleSendAndResolve = () => {
    if (!messageText.trim() && attachedFiles.length === 0) return

    onSendAndResolve({
      body: messageText,
      files: attachedFiles,
    })

    // Clear inputs after sending
    setMessageText("")
    setAttachedFiles([])
  }

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files)
    setAttachedFiles((prev) => [...prev, ...files])
    // Reset input to allow selecting the same file again
    e.target.value = ""
  }

  const handleAttachmentButtonClick = () => {
    // Clear the input value before opening file picker
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
    fileInputRef.current.click()
  }

  const removeFile = (index) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const handlePresetSelect = (presetText) => {
    setMessageText(presetText)
    setShowPresets(false)
  }

  // Function to scroll left
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -200, behavior: "smooth" })
    }
  }

  // Function to scroll right
  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 200, behavior: "smooth" })
    }
  }

  // Check for overflow and update gradient visibility
  const updateGradients = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current
      setShowLeftGradient(scrollLeft > 0)
      setShowRightGradient(scrollLeft < scrollWidth - clientWidth - 1)
    }
  }

  useEffect(() => {
    updateGradients()
    window.addEventListener("resize", updateGradients)
    return () => window.removeEventListener("resize", updateGradients)
  }, [attachedFiles])

  const handleScroll = () => {
    updateGradients()
  }

  const isButtonDisabled =
    sending || (!messageText.trim() && attachedFiles.length === 0)

  return (
    <div className="border border-admin_bordered rounded-md bg-admin_paper p-4">
      {/* Message Input */}
      <div className="relative">
        <textarea
          className="w-full p-4 bg-admin_canvas text-admin_text  border border-admin_bordered rounded-lg resize-none focus:outline-none focus:border-admin_primary"
          rows="3"
          value={messageText}
          onChange={(e) => setMessageText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type your message..."
          disabled={sending}
        />

        {/* Bottom Bar with buttons and files */}
        <div className="flex flex-col gap-2 mt-1">
          {/* Buttons Row */}
          <div className="flex items-center justify-between">
            <div className="flex gap-2 flex-shrink-0">
              <Button variant="admin_primary" onClick={() => setShowPresets(true)}>
                Responses
              </Button>

              <Button
                variant="admin_secondary"
                className="flex items-center gap-2"
                onClick={handleAttachmentButtonClick}
              >
                <PaperClipIcon className="w-5 h-5" />
                <span className="hidden xl:block">Attachments</span>
              </Button>

              {/* File browser */}
              <Input
                variant="admin"
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={handleFileSelect}
              />
            </div>

            <div className="flex items-center gap-2">
              <Typography variant="admin_txt2_dim" className="hidden xl:block my-0">
                Press {isMac ? "CMD" : "Ctrl"} + Enter to Send
              </Typography>

              {/* Regular Send Button */}
              <Button
                variant="admin_primary"
                onClick={handleSend}
                disabled={isButtonDisabled}
                className="disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap relative"
                title="Send"
              >
                {sending ? (
                  <Spinner variant="button" size="sm" />
                ) : (
                  <PaperAirplaneIcon className="w-5 h-5 transform rotate-0" />
                )}
              </Button>
              {/* Send and Resolve Button */}
              <Button
                variant="admin_primary_red"
                onClick={handleSendAndResolve}
                disabled={isButtonDisabled}
                className="disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
                title="Send and Resolve"
              >
                {sending ? (
                  <>
                    <Spinner variant="button" size="sm" />
                    <span>Sending...</span>
                  </>
                ) : (
                  <span>Send & Resolve</span>
                )}
              </Button>
            </div>
          </div>

          {/* Files Container Row*/}
          {attachedFiles.length > 0 && (
            <div className="flex-1 min-w-0 relative w-[470px] xl:w-[670px]">
              {/* Left gradient overlay */}
              {showLeftGradient && (
                <div
                  className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-admin_canvas via-admin_canvas/80 to-transparent z-10 flex items-center cursor-pointer"
                  onClick={scrollLeft}
                >
                  <ChevronLeftIcon className="w-5 h-5 text-admin_text_secondary ml-1" />
                </div>
              )}

              {/* Scrollable container */}
              <div
                ref={scrollContainerRef}
                className="overflow-hidden"
                onScroll={handleScroll}
                style={{
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                }}
              >
                <div className="flex gap-2 py-1">
                  {attachedFiles.map((file, index) => (
                    <div
                      key={index}
                      className={`flex items-center gap-2 px-3 py-1.5 border rounded-full text-sm whitespace-nowrap flex-shrink-0 ${
                        sending
                          ? "bg-admin_neutral_1 border-admin_primary/30"
                          : "bg-admin_paper border-admin_bordered"
                      }`}
                    >
                      {sending ? (
                        <Spinner variant="minimal" className="w-4 h-4" />
                      ) : (
                        <PaperClipIcon className="w-4 h-4 text-admin_text_secondary" />
                      )}

                      <Typography
                        variant="admin_txt1"
                        className={sending ? "text-admin_text" : "text-admin_text_secondary"}>
                        {file.name}
                      </Typography>

                      {!sending && (
                        <button
                          onClick={() => removeFile(index)}
                          className="ml-1 text-admin_text_secondary hover:text-admin_error"
                        >
                          <XMarkIcon className="w-4 h-4 stroke-2" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Right gradient overlay */}
              {showRightGradient && (
                <div
                  className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-admin_canvas via-admin_canvas/80 to-transparent z-10 flex items-center justify-end cursor-pointer"
                  onClick={scrollRight}
                >
                  <ChevronRightIcon className="w-5 h-5 text-admin_text_secondary mr-1" />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Presets Dialog */}
      {showPresets && (
        <ChatResponsePresets
          onClose={() => setShowPresets(false)}
          onSelect={handlePresetSelect}
        />
      )}

      {/* Add CSS for hiding scrollbar */}
      <style jsx="true">{`
        div::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  )
}

export default ChatArea
